import React, { ReactNode, useEffect, useState } from "react";
import { Avatar, Button, Input, Layout, Tooltip, Badge } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  HomeOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  FileTextOutlined,
  LogoutOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Notification } from "../components/Dashboard/Notification";
import SelectRole from "../components/Dashboard/SelectRole";
import ImpersonationBack from "../components/Dashboard/ImpersonationBack";
import HeaderBackToGate from "../components/HeaderBackToGate";
import { can } from "../utils";
import { hasAnyRole } from "../auth/roleAccess";
import logo from "../assets/logo.svg";
import "./modern-layout.css";

const { Sider, Content } = Layout;

let avatarSrc = require(`../assets/animojis/Number=1.png`);

interface CustomLayoutProps {
  children: ReactNode;
}

interface MenuItem {
  key: string;
  icon?: any;
  title: string;
  path?: string;
  roles: string[];
  permissions?: string[];
  children?: MenuItem[];
  badge?: number;
}

const MainLayout: React.FC<CustomLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<any>(avatarSrc);
  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const profilePhoto = localStorage.getItem("profilePhoto");
      if (profilePhoto !== null) {
        avatarSrc = require(`../assets/animojis/Number=${profilePhoto}.png`);
        setProfilePhoto(avatarSrc);
      }
    } catch (error) {
      console.error("Avatar yüklenirken bir hata oluştu:", error);
    }
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken === null) {
      navigate("/login");
    }
  }, [navigate]);

  const logout = () => {
    [
      "externalAccessToken",
      "externalRefreshToken",
      "accessToken",
      "refreshToken",
      "accessTokenExpiredAt",
      "refreshTokenExpiredAt",
      "customerFilterText",
      "retentionFilterText",
      "hi5CustomerFilterText",
      "hi5SuspendedCustomerFilterText",
      "eduLearnerFilterText",
      "dropshippingCampFilterText",
      "prospectFilterText",
      "softwareSuspendedFilterText",
      "amazonSuspendedFilterText",
      "masCustomerFilterText",
      "accountManagementFilterText",
      "impersonatorSession",
    ].forEach((item) => localStorage.removeItem(item));
    navigate("/login");
  };

  const clearFilters = (path: string) => {
    const filterExclusions: Record<string, string[]> = {
      "/edu-learners": ["eduLearnerFilterText"],
      "/dropshipping-camp": ["dropshippingCampFilterText"],
      "/mongerlab-customers": ["mongerlabCustomerFilterText"],
      "/customers": ["customerFilterText"],
      "/retention-customers": ["retentionFilterText"],
      "/hi5-customers": ["hi5CustomerFilterText"],
      "/hi5-suspended-customers": ["hi5SuspendedCustomerFilterText"],
      "/prospect-customers": ["prospectFilterText"],
      "/stm-suspended-customers": ["softwareSuspendedFilterText"],
      "/amazon-suspended-applicants": ["amazonSuspendedFilterText"],
      "/mas-customers": ["masCustomerFilterText"],
      "/account-management-customers": ["accountManagementFilterText"],
    };

    const allFilters = [
      "eduLearnerFilterText",
      "dropshippingCampFilterText",
      "mongerlabCustomerFilterText",
      "customerFilterText",
      "retentionFilterText",
      "hi5CustomerFilterText",
      "hi5SuspendedCustomerFilterText",
      "prospectFilterText",
      "softwareSuspendedFilterText",
      "amazonSuspendedFilterText",
      "masCustomerFilterText",
      "accountManagementFilterText",
    ];

    const exclusions = filterExclusions[path] || [];
    const filtersToRemove = allFilters.filter(
      (filter) => !exclusions.includes(filter)
    );
    filtersToRemove.forEach((filter) => localStorage.removeItem(filter));
  };

  // Menü yapısı - kategorilere ayrılmış
  const menuStructure: MenuItem[] = [
    {
      key: "home",
      icon: <HomeOutlined />,
      title: "Home",
      path: "/",
      roles: [
        "ADMIN",
        "ONLY_SEE_CUSTOMER",
        "MARKETING_TEAM_MANAGER",
        "CALL_TEAM_MANAGER",
        "CALL_TEAM",
        "ACCOUNT_MANAGER_MANAGER",
        "ACCOUNT_MANAGER",
        "HI5_ACM_MANAGER",
        "HI5_ACM_MEMBER",
        "SUCCESS_TEAM_MANAGER",
        "SUCCESS_TEAM",
        "SUSPEND_TEAM_MANAGER",
        "SUSPEND_TEAM",
        "MAS_TEAM_MANAGER",
        "MAS_TEAM_MEMBER",
        "ACCOUNT_MANAGEMENT_MANAGER",
        "ACCOUNT_MANAGEMENT_MEMBER",
        "HI5_ACCOUNT_MANAGER",
      ],
      permissions: ["users:getAuthUser"],
    },
    {
      key: "task",
      icon: <FileTextOutlined />,
      title: "Task",
      path: "/task",
      roles: [
        "ADMIN",
        "CALL_TEAM_MANAGER",
        "CALL_TEAM",
        "ACCOUNT_MANAGER_MANAGER",
        "ACCOUNT_MANAGER",
        "HI5_ACM_MANAGER",
        "HI5_ACM_MEMBER",
        "HI5_ACCOUNT_MANAGER",
        "SUCCESS_TEAM_MANAGER",
        "SUCCESS_TEAM",
        "SUSPEND_TEAM_MANAGER",
        "SUSPEND_TEAM",
        "MAS_TEAM_MANAGER",
        "MAS_TEAM_MEMBER",
      ],
    },
    {
      key: "edu",
      icon: <UserOutlined />,
      title: "EDU",
      roles: ["ADMIN", "SUCCESS_TEAM_MANAGER", "SUCCESS_TEAM", "SUCCESS_TEAM_REPORTER"],
      children: [
        {
          key: "edu-learners",
          title: "Edu Learners",
          path: "/edu-learners",
          roles: ["ADMIN", "SUCCESS_TEAM_MANAGER", "SUCCESS_TEAM", "SUCCESS_TEAM_REPORTER"],
        },
        {
          key: "dropshipping-camp",
          title: "Dropshipping Camp",
          path: "/dropshipping-camp",
          roles: ["ADMIN", "SUCCESS_TEAM_MANAGER", "SUCCESS_TEAM", "SUCCESS_TEAM_REPORTER"],
        },
      ],
    },
    {
      key: "customers",
      icon: <TeamOutlined />,
      title: "Customers",
      roles: [
        "ADMIN",
        "ACCOUNT_MANAGER",
        "ACCOUNT_MANAGER_MANAGER",
        "CALL_TEAM",
        "CALL_TEAM_MANAGER",
        "ONLY_SEE_CUSTOMER",
        "MARKETING_TEAM_MANAGER",
        "HI5_ACM_MANAGER",
        "HI5_ACM_MEMBER",
        "SUCCESS_TEAM_MANAGER",
        "SUCCESS_TEAM",
        "SUCCESS_TEAM_REPORTER",
        "MAS_TEAM_MANAGER",
        "MAS_TEAM_MEMBER",
        "ACCOUNT_MANAGEMENT_MANAGER",
        "ACCOUNT_MANAGEMENT_MEMBER",
        "HI5_ACCOUNT_MANAGER",
      ],
      children: [
        {
          key: "customers-list",
          title: "Customers",
          path: "/customers",
          roles: [
            "ADMIN",
            "ACCOUNT_MANAGER",
            "ACCOUNT_MANAGER_MANAGER",
            "CALL_TEAM",
            "CALL_TEAM_MANAGER",
            "ONLY_SEE_CUSTOMER",
            "MARKETING_TEAM_MANAGER",
          ],
        },
        {
          key: "prospect-customers",
          title: "Call Center Customers",
          path: "/prospect-customers",
          roles: ["ADMIN", "CALL_TEAM", "CALL_TEAM_MANAGER", "MARKETING_TEAM_MANAGER"],
        },
        {
          key: "hi5-customers",
          title: "Hi5 Customers",
          path: "/hi5-customers",
          roles: ["ADMIN", "HI5_ACM_MANAGER", "HI5_ACM_MEMBER"],
        },
        {
          key: "hi5-suspended-customers",
          title: "Hi5 Suspended Customers",
          path: "/hi5-suspended-customers",
          roles: ["ADMIN", "HI5_ACM_MANAGER", "HI5_ACM_MEMBER"],
        },
        {
          key: "mongerlab-customers",
          title: "Mongerlab Customers",
          path: "/mongerlab-customers",
          roles: ["ADMIN", "MARKETING_TEAM_MANAGER", "MARKETING_USER"],
          permissions: ["mongerlab-customers:getAll"],
        },
        {
          key: "stm-suspended-customers",
          title: "Software Suspended Customers",
          path: "/stm-suspended-customers",
          roles: ["ADMIN", "SUCCESS_TEAM", "SUCCESS_TEAM_MANAGER", "SUCCESS_TEAM_REPORTER"],
        },
        {
          key: "mas-customers",
          title: "MAS Customers",
          path: "/mas-customers",
          roles: ["ADMIN", "MAS_TEAM_MANAGER", "MAS_TEAM_MEMBER"],
        },
        {
          key: "account-management-customers",
          title: "Account Management",
          path: "/account-management-customers",
          roles: ["ADMIN", "HI5_ACCOUNT_MANAGER", "HI5_ACCOUNT_MEMBER", "MARKETING_USER"],
          permissions: ["account-management-customers:getAll"],
        },
      ],
    },
    {
      key: "suspend-team",
      icon: <TeamOutlined />,
      title: "Suspend Team",
      roles: ["ADMIN", "SUSPEND_TEAM", "SUSPEND_TEAM_MANAGER"],
      children: [
        {
          key: "amazon-suspended-applicants",
          title: "Amazon Suspended Applicants",
          path: "/amazon-suspended-applicants",
          roles: ["ADMIN", "SUSPEND_TEAM", "SUSPEND_TEAM_MANAGER"],
        },
      ],
    },
    {
      key: "lists",
      icon: <FileTextOutlined />,
      title: "Lists",
      roles: [
        "ADMIN",
        "SUCCESS_TEAM",
        "SUCCESS_TEAM_MANAGER",
        "SUCCESS_TEAM_REPORTER",
        "ACCOUNT_MANAGER_MANAGER",
        "ACCOUNT_MANAGER",
        "HI5_ACM_MANAGER",
        "HI5_ACM_MEMBER",
        "ACCOUNT_MANAGEMENT_SHIPMENT",
      ],
      children: [
        {
          key: "retention-customers",
          title: "Retention Customer List",
          path: "/retention-customers",
          roles: ["ADMIN", "SUCCESS_TEAM", "SUCCESS_TEAM_MANAGER", "SUCCESS_TEAM_REPORTER"],
        },
        {
          key: "dropout-feedback",
          title: "Dropout Feedback List",
          path: "/dropout-feedback",
          roles: [
            "ADMIN",
            "ACCOUNT_MANAGER_MANAGER",
            "ACCOUNT_MANAGER",
            "HI5_ACM_MANAGER",
            "HI5_ACM_MEMBER",
            "SUCCESS_TEAM_MANAGER",
            "SUCCESS_TEAM",
          ],
        },
        {
          key: "customer-orders-list",
          title: "Customer Orders List",
          path: "/account-management-shipments/customer-orders-list",
          roles: ["ADMIN", "ACCOUNT_MANAGEMENT_SHIPMENT"],
        },
        {
          key: "customer-shipments-list",
          title: "Customer Shipments List",
          path: "/account-management-shipments/customer-shipments-list",
          roles: ["ADMIN", "ACCOUNT_MANAGEMENT_SHIPMENT"],
        },
      ],
    },
    {
      key: "reports",
      icon: <FileTextOutlined />,
      title: "Reports",
      roles: [
        "ADMIN",
        "ACCOUNT_MANAGER_MANAGER",
        "SUCCESS_TEAM_MANAGER",
        "HI5_ACM_MANAGER",
        "CALL_TEAM_MANAGER",
        "SUSPEND_TEAM_MANAGER",
        "MAS_TEAM_MANAGER",
        "ACCOUNT_MANAGEMENT_MANAGER",
        "MARKETING_TEAM_MANAGER",
      ],
      children: [
        {
          key: "acm-reports",
          title: "ACM Reports",
          roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"],
          children: [
            { key: "acm-call-history", title: "Call History", path: "/reports/acm-reports/call-history", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-status", title: "Status", path: "/reports/acm-reports/status", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-current-packages", title: "Current Packages", path: "/reports/acm-reports/current-packages", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-earnings", title: "Earnings Report", path: "/reports/acm-reports/earnings-report", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-sales-log", title: "Sales Log Report", path: "/reports/acm-reports/sales-log-report", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-summary", title: "Summary Report", path: "/reports/acm-reports/summary-report", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-package-interest", title: "Package of Interest", path: "/reports/acm-reports/package-of-interest-for-acm", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "acm-current-package-interest", title: "Current Package of Interest", path: "/reports/acm-reports/current-package-of-interest-for-acm", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
          ],
        },
        {
          key: "hi5-acm-reports",
          title: "Hi5 ACM Reports",
          roles: ["ADMIN", "HI5_ACM_MANAGER"],
          children: [
            { key: "hi5-call-history", title: "Call History", path: "/reports/hi5-acm-reports/call-history", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-status", title: "Status", path: "/reports/hi5-acm-reports/status", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-current-packages", title: "Current Packages", path: "/reports/hi5-acm-reports/current-packages", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-earnings", title: "Earnings Report", path: "/reports/hi5-acm-reports/earnings-report", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-sales-log", title: "Sales Log Report", path: "/reports/hi5-acm-reports/sales-log-report", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-summary", title: "Summary Report", path: "/reports/hi5-acm-reports/summary-report", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-package-interest", title: "Package of Interest", path: "/reports/hi5-acm-reports/package-of-interest-for-hi5-acm", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-current-package-interest", title: "Current Package of Interest", path: "/reports/hi5-acm-reports/current-package-of-interest-for-hi5-acm", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "hi5-lessons", title: "Lessons", path: "/reports/hi5-acm-reports/lessons", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
          ],
        },
        {
          key: "stm-reports",
          title: "STM Reports",
          roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"],
          children: [
            { key: "stm-learner-status", title: "Learner Status", path: "/reports/stm-reports/learner-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-retention-status", title: "Retention Status", path: "/reports/stm-reports/retention-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-software-suspended", title: "Software Suspended Status", path: "/reports/stm-reports/software-suspended-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-education", title: "Education", path: "/reports/stm-reports/education", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-success-team", title: "Success Team", path: "/reports/stm-reports/success-team", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-call-history", title: "Call History", path: "/reports/stm-reports/call-history", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-affiliate", title: "Affiliate", path: "/reports/stm-reports/affiliate", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-earnings", title: "Earnings Report", path: "/reports/stm-reports/earnings-report", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-sales-log", title: "Sales Log Report", path: "/reports/stm-reports/sales-log-report", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-summary", title: "Summary Report", path: "/reports/stm-reports/summary-report", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-package-interest", title: "Package of Interest", path: "/reports/stm-reports/package-of-interest-for-stm", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "stm-old-package-interest", title: "Old Package of Interest", path: "/reports/stm-reports/old-package-of-interest-for-stm", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
          ],
        },
        {
          key: "ctm-reports",
          title: "CTM Reports",
          roles: ["ADMIN", "CALL_TEAM_MANAGER"],
          children: [
            { key: "ctm-call-history", title: "Call History", path: "/reports/ctm-reports/call-history", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "ctm-earnings", title: "Earnings Report", path: "/reports/ctm-reports/earnings-report", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "ctm-sales-log", title: "Sales Log Report", path: "/reports/ctm-reports/sales-log-report", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "ctm-summary", title: "Summary Report", path: "/reports/ctm-reports/summary-report", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
          ],
        },
        {
          key: "suspend-reports",
          title: "Suspend Team Reports",
          roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"],
          children: [
            { key: "suspend-applicant-reports", title: "Amazon Suspended Applicant Reports", path: "/reports/suspendtm-reports/amazon-suspended-applicant-reports", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "suspend-status-report", title: "Amazon Suspended Applicant Status Report", path: "/reports/suspendtm-reports/amazon-suspended-applicant-status-report", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "suspend-violations-paid", title: "Number of Violations Paid Report", path: "/reports/suspendtm-reports/number-of-violations-paid-report", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
          ],
        },
        {
          key: "mas-reports",
          title: "MAS Reports",
          roles: ["ADMIN", "MAS_TEAM_MANAGER"],
          children: [
            { key: "mas-summary", title: "Summary Report", path: "/reports/mastm-reports/summary-report", roles: ["ADMIN", "MAS_TEAM_MANAGER"] },
            { key: "mas-earnings", title: "Earnings Report", path: "/reports/mastm-reports/earnings-report", roles: ["ADMIN", "MAS_TEAM_MANAGER"] },
          ],
        },
        {
          key: "account-management-reports",
          title: "Account Management Reports",
          roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"],
          children: [
            { key: "am-profit-loss", title: "Profit and Loss Report", path: "/reports/account-management-tm-reports/profit-and-loss-report", roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"] },
            { key: "am-customer-profit-loss", title: "Customer Profit and Loss Report", path: "/reports/account-management-tm-reports/customer-profit-and-loss-report", roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"] },
          ],
        },
        {
          key: "marketing-reports",
          title: "Marketing Reports",
          roles: ["ADMIN", "MARKETING_TEAM_MANAGER"],
          children: [
            { key: "marketing-summary", title: "Summary Report", path: "/reports/marketingtm-reports/summary-report", roles: ["ADMIN", "MARKETING_TEAM_MANAGER"] },
          ],
        },
        {
          key: "common-reports",
          title: "Common Reports",
          roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER", "SUCCESS_TEAM_MANAGER", "HI5_ACM_MANAGER", "CALL_TEAM_MANAGER", "SUSPEND_TEAM_MANAGER", "MAS_TEAM_MANAGER", "ACCOUNT_MANAGEMENT_MANAGER", "MARKETING_TEAM_MANAGER"],
          children: [
            { key: "feedback-report", title: "Feedback Report", path: "/reports/common-reports/feedback-report", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER", "SUCCESS_TEAM_MANAGER", "HI5_ACM_MANAGER", "CALL_TEAM_MANAGER", "SUSPEND_TEAM_MANAGER", "MAS_TEAM_MANAGER", "ACCOUNT_MANAGEMENT_MANAGER", "MARKETING_TEAM_MANAGER"] },
          ],
        },
      ],
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      title: "Settings",
      roles: [
        "ADMIN",
        "SUCCESS_TEAM_MANAGER",
        "CALL_TEAM_MANAGER",
        "ACCOUNT_MANAGER_MANAGER",
        "HI5_ACM_MANAGER",
        "SUSPEND_TEAM_MANAGER",
        "MAS_TEAM_MANAGER",
        "ACCOUNT_MANAGEMENT_MANAGER",
      ],
      children: [
        {
          key: "stm-settings",
          title: "Success Team Settings",
          roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"],
          children: [
            { key: "learner-status", title: "Learner Status", path: "/settings/learner-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "retention-status", title: "Retention Status", path: "/settings/retention-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "software-suspend-status", title: "Software Suspend Status", path: "/settings/software-suspend-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "reason-dropout-stm", title: "Reason for Dropout Status", path: "/settings/reason-for-dropout-status", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "package-interest-stm", title: "Package of Interest Status", path: "/settings/package-of-interest-status-for-stm", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "old-package-interest-stm", title: "Old Package of Interest Status", path: "/settings/old-package-of-interest-status-for-stm", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "bonus-stm", title: "Bonus Policy", path: "/settings/bonus", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
            { key: "affiliate-stm", title: "Affiliate", path: "/settings/affiliate", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER"] },
          ],
        },
        {
          key: "ctm-settings",
          title: "Call Team Settings",
          roles: ["ADMIN", "CALL_TEAM_MANAGER"],
          children: [
            { key: "call-status", title: "Call Status", path: "/settings/call-status", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "call-center-detail-status", title: "Call Center Detail Status", path: "/settings/call-center-detail-status", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "call-sales-package", title: "Call Sales Package", path: "/settings/call-sales-package", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "call-sales-package-servey", title: "Call Sales Package Survey", path: "/settings/call-sales-package-servey", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "prospect-customer-sources", title: "Prospect Customer Sources", path: "/settings/prospect-customer-sources", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
            { key: "prospect-customer-campaigns", title: "Prospect Customer Campaigns", path: "/settings/prospect-customer-campaigns", roles: ["ADMIN", "CALL_TEAM_MANAGER"] },
          ],
        },
        {
          key: "acm-settings",
          title: "ACM Settings",
          roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"],
          children: [
            { key: "acm-status", title: "ACM Status", path: "/settings/acm-status", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "reason-dropout-acm", title: "Reason for Dropout Status", path: "/settings/reason-for-dropout-status", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "package-interest-acm", title: "Package of Interest Status", path: "/settings/package-of-interest-status-for-acm", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
            { key: "current-package-interest-acm", title: "Current Package of Interest Status", path: "/settings/current-package-of-interest-status-for-acm", roles: ["ADMIN", "ACCOUNT_MANAGER_MANAGER"] },
          ],
        },
        {
          key: "hi5-acm-settings",
          title: "Hi5 ACM Settings",
          roles: ["ADMIN", "HI5_ACM_MANAGER"],
          children: [
            { key: "hi5-acm-status", title: "Hi5 ACM Status", path: "/settings/hi5-acm-status", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "reason-dropout-hi5", title: "Reason for Dropout Status", path: "/settings/reason-for-dropout-status", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "package-interest-hi5", title: "Package of Interest Status", path: "/settings/package-of-interest-status-for-hi5-acm", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
            { key: "current-package-interest-hi5", title: "Current Package of Interest Status", path: "/settings/current-package-of-interest-status-for-hi5-acm", roles: ["ADMIN", "HI5_ACM_MANAGER"] },
          ],
        },
        {
          key: "suspend-settings",
          title: "Suspend Team Settings",
          roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"],
          children: [
            { key: "amazon-suspended-status", title: "Amazon Suspended Applicant Status", path: "/settings/amazon-suspended-applicant-status", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "amazon-suspended-suspend-types", title: "Amazon Suspended Applicant Suspend Types", path: "/settings/amazon-suspended-applicant-suspend-types", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "amazon-suspended-service-types", title: "Amazon Suspended Applicant Service Types", path: "/settings/amazon-suspended-applicant-service-types", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "amazon-suspended-action-types", title: "Amazon Suspended Applicant Action Types", path: "/settings/amazon-suspended-applicant-action-types", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
            { key: "amazon-suspended-inputs", title: "Amazon Suspended Applicant Inputs", path: "/settings/amazon-suspended-applicant-inputs", roles: ["ADMIN", "SUSPEND_TEAM_MANAGER"] },
          ],
        },
        {
          key: "mas-settings",
          title: "MAS Settings",
          roles: ["ADMIN", "MAS_TEAM_MANAGER"],
          children: [
            { key: "feedback-status", title: "Feedback Status", path: "/settings/feedback-status", roles: ["ADMIN", "MAS_TEAM_MANAGER"] },
            { key: "feedback-channel", title: "Feedback Channel", path: "/settings/feedback-channel", roles: ["ADMIN", "MAS_TEAM_MANAGER"] },
            { key: "checklist-status", title: "Checklist Status", path: "/settings/checklist-status", roles: ["ADMIN", "MAS_TEAM_MANAGER"] },
          ],
        },
        {
          key: "account-management-settings",
          title: "Account Management Settings",
          roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"],
          children: [
            { key: "am-feedback-status", title: "Feedback Status", path: "/settings/feedback-status", roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"] },
            { key: "am-feedback-channel", title: "Feedback Channel", path: "/settings/feedback-channel", roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"] },
            { key: "am-checklist-status", title: "Checklist Status", path: "/settings/checklist-status", roles: ["ADMIN", "ACCOUNT_MANAGEMENT_MANAGER"] },
          ],
        },
        {
          key: "general-settings",
          title: "General Settings",
          roles: ["ADMIN", "SUCCESS_TEAM_MANAGER", "CALL_TEAM_MANAGER", "ACCOUNT_MANAGER_MANAGER", "HI5_ACM_MANAGER", "SUSPEND_TEAM_MANAGER", "MAS_TEAM_MANAGER", "ACCOUNT_MANAGEMENT_MANAGER"],
          children: [
            { key: "users", title: "Users", path: "/settings/users", roles: ["ADMIN", "SUCCESS_TEAM_MANAGER", "CALL_TEAM_MANAGER", "ACCOUNT_MANAGER_MANAGER", "HI5_ACM_MANAGER", "SUSPEND_TEAM_MANAGER", "MAS_TEAM_MANAGER", "ACCOUNT_MANAGEMENT_MANAGER"] },
            { key: "roles", title: "Roles", path: "/settings/roles", roles: ["ADMIN"] },
            { key: "groups", title: "User Groups", path: "/settings/groups", roles: ["ADMIN"] },
            { key: "departments", title: "Departments", path: "/settings/departments", roles: ["ADMIN"] },
            { key: "employees", title: "Employees", path: "/settings/employees", roles: ["ADMIN"] },
            { key: "jobs", title: "Jobs", path: "/settings/jobs", roles: ["ADMIN"] },
            { key: "mongerlab-customer-status", title: "Mongerlab CRM Status", path: "/settings/mongerlab-customer-status", roles: ["ADMIN"], permissions: ["mongerlab-customer-statuses:create", "mongerlab-customer-statuses:update"] },
            { key: "mongerlab-customer-roles", title: "Mongerlab Roles", path: "/settings/mongerlab-customer-roles", roles: ["ADMIN"], permissions: ["mongerlab-customer-roles:update"] },
            { key: "mongerlab-customer-app-status", title: "Mongerlab App Status", path: "/settings/mongerlab-customer-app-status", roles: ["ADMIN"], permissions: ["mongerlab-customer-app-statuses:update"] },
          ],
        },
      ],
    },
  ];

  const hasMenuAccess = (item: MenuItem) => {
    const permissionMatch =
      item.permissions?.some((permission) => can(permission)) ?? false;
    const roleMatch = hasAnyRole(item.roles);
    if (item.permissions?.length) {
      return permissionMatch || roleMatch;
    }
    return roleMatch;
  };

  const filterMenuByAccess = (items: MenuItem[]): MenuItem[] => {
    return items
      .map((item) => ({
        ...item,
        children: item.children ? filterMenuByAccess(item.children) : undefined,
      }))
      .filter((item) => {
        if (item.children?.length) {
          return item.children.length > 0;
        }
        return hasMenuAccess(item);
      });
  };

  // Arama fonksiyonu
  const filterMenuBySearch = (items: MenuItem[], search: string): MenuItem[] => {
    if (!search) return items;

    const filtered: MenuItem[] = [];
    
    items.forEach((item) => {
      const matchesSearch = item.title
        .toLowerCase()
        .includes(search.toLowerCase());
      const filteredChildren = item.children
        ? filterMenuBySearch(item.children, search)
        : [];

      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : item.children,
        });
      }
    });
    
    return filtered;
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleMenuClick = (path: string) => {
    clearFilters(path);
    navigate(path);
  };

  const filteredMenu = filterMenuBySearch(
    filterMenuByAccess(menuStructure),
    searchText
  );

  // Arama yapıldığında tüm kategorileri aç
  useEffect(() => {
    if (searchText) {
      const allKeys = filteredMenu
        .filter((item) => item.children)
        .map((item) => item.key);
      setExpandedKeys(allKeys);
    }
  }, [searchText]);

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const isActive = location.pathname === item.path;
    const isExpanded = expandedKeys.includes(item.key);
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div key={item.key} className="menu-group">
          <div
            className={`menu-item menu-group-title ${isExpanded ? "expanded" : ""}`}
            onClick={() => toggleExpand(item.key)}
            style={{ paddingLeft: `${16 + level * 16}px` }}
          >
            <span className="menu-item-icon">{item.icon}</span>
            {!collapsed && (
              <>
                <span className="menu-item-title">{item.title}</span>
                <span className="menu-item-arrow">
                  {isExpanded ? <DownOutlined /> : <RightOutlined />}
                </span>
              </>
            )}
          </div>
          {!collapsed && isExpanded && (
            <div className="menu-children">
              {item.children?.map((child) => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={item.key}
        className={`menu-item ${isActive ? "active" : ""}`}
        onClick={() => item.path && handleMenuClick(item.path)}
        style={{ paddingLeft: `${16 + level * 16}px` }}
      >
        {item.icon && <span className="menu-item-icon">{item.icon}</span>}
        {!collapsed && <span className="menu-item-title">{item.title}</span>}
        {item.badge && !collapsed && (
          <Badge count={item.badge} className="menu-item-badge" />
        )}
      </div>
    );
  };

  return (
    <Layout className="modern-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={280}
        className="modern-sider"
        breakpoint="lg"
        collapsedWidth={80}
      >
        <div className="sider-content">
          <div className="sider-header">
            <Link to="/" className="logo-container">
              <img src={logo} alt="logo" className="logo" />
            </Link>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="collapse-btn"
            />
          </div>

          {!collapsed && (
            <div className="search-container">
              <Input
                placeholder="Search menu..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </div>
          )}

          <div className="menu-container">
            {filteredMenu.map((item) => renderMenuItem(item))}
          </div>

          <div className="sider-footer">
            <Tooltip placement="right" title="Profile">
              <Link
                to="/profile"
                className="profile-link"
                onClick={() => clearFilters("/profile")}
              >
                <Avatar size={collapsed ? 40 : 48} src={profilePhoto} />
                {!collapsed && (
                  <span className="profile-name">
                    {localStorage.getItem("userName") || "Profile"}
                  </span>
                )}
              </Link>
            </Tooltip>
            <Tooltip placement="right" title="Logout">
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={logout}
                className="logout-btn"
                danger
              >
                {!collapsed && "Logout"}
              </Button>
            </Tooltip>
          </div>
        </div>
      </Sider>
      <Layout className="modern-layout-content">
        <Content className="modern-content">
          <div className="content-header">
            <ImpersonationBack />
            <HeaderBackToGate />
            <SelectRole />
            <Notification />
          </div>
          <div className="content-body">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
